"use client";

import { useEffect } from "react";
import { type ScriptProps } from "next/script";
import { useUser } from "@clerk/nextjs";

import { getUserPlanByMetadata } from "@/helper/user/client";

declare global {
  interface Window {
    adsbygoogle: unknown[];
  }
}

interface AdSenseScriptProps extends ScriptProps {
  shouldCheckPlan?: boolean;
}

export function AdSenseScript({
  children,
  shouldCheckPlan,
  ...props
}: AdSenseScriptProps) {
  try {
    if (shouldCheckPlan) {
      const { user } = useUser();
      const { hasPaidPlan } = getUserPlanByMetadata(user?.publicMetadata);

      useEffect(() => {
        const removeAdsenseContent = () => {
          if (typeof window === "undefined") return;

          // Helper function to safely remove elements
          const removeElements = (selector: string) => {
            try {
              const elements = document.querySelectorAll(selector);
              elements.forEach((el) => {
                if (el?.parentElement) {
                  el.parentElement.removeChild(el);
                }
              });
            } catch (err) {
              console.error(
                `Erro ao remover elementos com o seletor ${selector}:`,
                err
              );
            }
          };

          try {
            // Remove all AdSense related elements
            removeElements('script[src*="adsbygoogle"]');
            removeElements('link[rel="preload"][href*="adsbygoogle"]');
            removeElements(".adsbygoogle");

            // Reset adsbygoogle array
            if ("adsbygoogle" in window) {
              window.adsbygoogle = [];
            }

            // Clear AdSense related caches
            if ("caches" in window) {
              caches
                .keys()
                .then((names) => {
                  names.forEach((name) => {
                    caches
                      .open(name)
                      .then((cache) => {
                        cache
                          .keys()
                          .then((requests) => {
                            requests.forEach((request) => {
                              if (request.url.includes("adsbygoogle")) {
                                cache.delete(request).catch((err) => {
                                  console.error("Erro ao deletar cache:", err);
                                });
                              }
                            });
                          })
                          .catch((err) => {
                            console.error(
                              "Erro ao obter chaves do cache:",
                              err
                            );
                          });
                      })
                      .catch((err) => {
                        console.error("Erro ao abrir cache:", err);
                      });
                  });
                })
                .catch((err) => {
                  console.error("Erro ao obter nomes do cache:", err);
                });
            }
          } catch (error) {
            console.error("Erro ao remover conteúdo do AdSense:", error);
          }
        };

        if (hasPaidPlan) {
          removeAdsenseContent();
        }
      }, [hasPaidPlan]);

      if (hasPaidPlan) return null;
    }

    return (
      <>
        <script
          async
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1764245778079932"
          crossOrigin="anonymous"
          data-ad-client="ca-pub-1764245778079932"
          {...props}
        />
        {children}
      </>
    );
  } catch (error) {
    console.error("Error in AdSenseScript component:", error);
    return null;
  }
}
